import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Upload, QrCode, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function fileToDataUrl(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

export default function HospitalPaymentQrSettings() {
  const { user } = useAuth();
  const isDoctor = user?.role === "doctor";
  // Doctors manage their own QR; admins manage the clinic-wide default.
  const endpoint = isDoctor ? "/doctor-payment-qr" : "/hospital-payment-qr";
  const isAdmin = ["admin", "hospital_admin", "doctor"].includes(user?.role);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    qr_image_url: "",
    payment_account_name: "",
    payment_instructions: "",
    consultation_fee_default: 0,
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setForm({
        qr_image_url: data.qr_image_url || "",
        payment_account_name: data.payment_account_name || "",
        payment_instructions: data.payment_instructions || "",
        consultation_fee_default: data.consultation_fee_default || 0,
      });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4 MB image"); return; }
    try {
      const url = await fileToDataUrl(f);
      setForm((s) => ({ ...s, qr_image_url: url }));
    } catch { toast.error("Couldn't read file"); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(endpoint, {
        ...form,
        consultation_fee_default: Number(form.consultation_fee_default || 0),
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="hospital-payment-qr-page">
      <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>{isDoctor ? "My Payment QR" : "Clinic Payment QR"}</h1>
      <p className="text-sm text-slate-500 mt-1">
        {isDoctor
          ? "Patients who book you online will scan this QR to pay upfront. If you leave it blank, the clinic's default payment QR is used instead."
          : "Default QR for the clinic. Patients booking online scan it to pay upfront, unless their doctor has set their own. Reception verifies the receipt to confirm the reservation."}
      </p>

      <Card className="mt-5 p-5 space-y-4">
        <div className="flex items-start gap-5">
          <div className="w-44 h-44 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center overflow-hidden">
            {form.qr_image_url ? (
              <img src={form.qr_image_url} alt="QR" className="max-h-full max-w-full object-contain" />
            ) : (
              <QrCode size={42} className="text-slate-300" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <div className="text-sm font-semibold text-slate-700">Payment QR image</div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="qr-file" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!isAdmin}>
              <Upload size={14} className="mr-1" />Upload QR image
            </Button>
            {!isAdmin && <div className="text-xs text-slate-500">You don't have permission to change this.</div>}
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Account name</label>
          <Input value={form.payment_account_name} onChange={(e) => setForm({ ...form, payment_account_name: e.target.value })} placeholder="e.g. Beltran Clinic GCash" disabled={!isAdmin} data-testid="qr-account" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Default consultation fee (PHP)</label>
          <Input type="number" value={form.consultation_fee_default} onChange={(e) => setForm({ ...form, consultation_fee_default: e.target.value })} disabled={!isAdmin} data-testid="qr-fee" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Payment instructions</label>
          <textarea value={form.payment_instructions} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} disabled={!isAdmin}
            placeholder="e.g. Send via GCash, screenshot the receipt, then upload it."
            className="mt-1 w-full border border-slate-300 rounded-lg p-3 min-h-[100px]" data-testid="qr-instructions" />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !isAdmin} className="bg-blue-700 hover:bg-blue-800" data-testid="qr-save"><Save size={14} className="mr-1" />Save</Button>
        </div>
      </Card>
    </div>
  );
}
